import React, { useState } from "react";
import PropTypes from "prop-types";
import Alert from "react-s-alert";
import DatePicker from "react-datepicker";
import moment from "moment";

import InputBox from "../reusable/InputBox/InputBox.jsx";
import { formatCurrency } from "../reusable/Utilities/format.js";
import { URLS as apiUrls } from "./../reusable/Utilities/urls";
import DocumentLinkList from "../reusable/DocumentLinkList/DocumentLinkList.jsx";
import SelectBox from "../reusable/SelectBox/SelectBox.jsx";
import { CONSTANTS } from "../reusable/Constants/constants.js";
import { userHasRole } from "../reusable/Utilities/userHasRole";
import If from "../reusable/If/If";
import ConfirmationModal from "../reusable/ConfirmationModal/ConfirmationModal.jsx";

import { json as ajax } from "./../../ajax.js";
import Modal from "../../Modal.jsx";

import FacilityInformation from "./FacilityInformation.jsx";
import AttachmentModal from "./FileAttachment.jsx";
import ChargeBackPaymentInfo from "./ChargeBackPaymentInfo.jsx";
import PayeeInformation from "./PayeeInformation";
import dateFormat from "./../reusable/Utilities/dateFormat.js";
import hstore from '../reusable/Utilities/hstore.js';

// paid_date is a `timestamp` column read back with ::date, so the server works in UTC
// calendar days. Send the picked day as UTC midnight, and read it back as a calendar day,
// so the date shown before submit, the date saved, and the date shown on reopen all match
// regardless of the user's time zone (TS-18590).
const toPaidDate = (value) => moment.utc(moment(value).format('YYYY-MM-DD'));
const paidDateForPicker = (value) => moment(moment.utc(value).format('YYYY-MM-DD'));

const optionList = {
  claimStatusManager: [
    { name: CONSTANTS.CHARGEBACK_CLAIM_STATUS_DISPLAY_MAP.open, value: CONSTANTS.CHARGEBACK_CLAIM_STATUS_MAP.open, isDisabled: false },
    { name: CONSTANTS.CHARGEBACK_CLAIM_STATUS_DISPLAY_MAP.approved, value: CONSTANTS.CHARGEBACK_CLAIM_STATUS_MAP.approved, isDisabled: false },
    { name: CONSTANTS.CHARGEBACK_CLAIM_STATUS_DISPLAY_MAP.waitingForChargeback, value: CONSTANTS.CHARGEBACK_CLAIM_STATUS_MAP.waitingForChargeback, isDisabled: false },
    { name: CONSTANTS.CHARGEBACK_CLAIM_STATUS_DISPLAY_MAP.manualChargeback, value: CONSTANTS.CHARGEBACK_CLAIM_STATUS_MAP.manualChargeback, isDisabled: false },
    { name: CONSTANTS.CHARGEBACK_CLAIM_STATUS_DISPLAY_MAP.systemChargeback, value: CONSTANTS.CHARGEBACK_CLAIM_STATUS_MAP.systemChargeback, isDisabled: true },
    { name: CONSTANTS.CHARGEBACK_CLAIM_STATUS_DISPLAY_MAP.deactivated, value: CONSTANTS.CHARGEBACK_CLAIM_STATUS_MAP.deactivated, isDisabled: false },
  ],
  claimStatusAgent: [
    { name: CONSTANTS.CHARGEBACK_CLAIM_STATUS_DISPLAY_MAP.open, value: CONSTANTS.CHARGEBACK_CLAIM_STATUS_MAP.open, isDisabled: true },
    { name: CONSTANTS.CHARGEBACK_CLAIM_STATUS_DISPLAY_MAP.approved, value: CONSTANTS.CHARGEBACK_CLAIM_STATUS_MAP.approved, isDisabled: true },
    { name: CONSTANTS.CHARGEBACK_CLAIM_STATUS_DISPLAY_MAP.waitingForChargeback, value: CONSTANTS.CHARGEBACK_CLAIM_STATUS_MAP.waitingForChargeback, isDisabled: false },
    { name: CONSTANTS.CHARGEBACK_CLAIM_STATUS_DISPLAY_MAP.manualChargeback, value: CONSTANTS.CHARGEBACK_CLAIM_STATUS_MAP.manualChargeback, isDisabled: false },
    { name: CONSTANTS.CHARGEBACK_CLAIM_STATUS_DISPLAY_MAP.systemChargeback, value: CONSTANTS.CHARGEBACK_CLAIM_STATUS_MAP.systemChargeback, isDisabled: true },
    { name: CONSTANTS.CHARGEBACK_CLAIM_STATUS_DISPLAY_MAP.deactivated, value: CONSTANTS.CHARGEBACK_CLAIM_STATUS_MAP.deactivated, isDisabled: false },
  ],
  payType: [
    { name: CONSTANTS.PAYMENT_TYPE_NAMES.DEFAULT, value: "" },
    { name: CONSTANTS.PAYMENT_TYPE_NAMES.CREDIT_CARD, value: CONSTANTS.PAYMENT_TYPE_CODES.CREDIT_CARD },
    { name: CONSTANTS.PAYMENT_TYPE_NAMES.CUSTOMER, value: CONSTANTS.PAYMENT_TYPE_CODES.CUSTOMER },
    { name: CONSTANTS.PAYMENT_TYPE_NAMES.STORE, value: CONSTANTS.PAYMENT_TYPE_CODES.STORE }
  ]
};

const ChargbackClaim = (props, context) => {
  const [showAttachmentModal, setShowAttchmentModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [initialStatus] = useState(props.claimObject.status);
  const [facility, setFacility] = useState(() => {});
  const [claimObject, setClaimObject] = useState(() => initialState());
  const [showUwConfirm, setShowUwConfirm] = useState(false);

  // Underwriting-period chargeback (TS-18343). The criteria result is computed from the
  // parent claim and is read-only here; only a manager can change the dropdown, at any
  // time regardless of chargeback status.
  const review = props.claimObject.underwriting_review || {};
  const isManager = userHasRole(props.user, CONSTANTS.USER_ROLES.autoClaimsManager);
  const UW = CONSTANTS.UNDERWRITING_CHARGEBACK_MAP;
  const underwritingOptions = [
    { name: UW.yes, value: UW.yes, isDisabled: false },
    { name: UW.no, value: UW.no, isDisabled: false },
    { name: UW.exception, value: UW.exception, isDisabled: false },
  ];

  // Red X when the figure meets the underwriting criteria, green check when it does not.
  const uwIcon = (meets) => (
    <i className={ `fa ${meets ? 'fa-times text-danger' : 'fa-check text-success'} ml-2` }
      aria-hidden="true"
      title={ meets ? 'Meets underwriting criteria' : 'Does not meet underwriting criteria' }/>
  );
  const isDisabled = (claimObject.status === CONSTANTS.CHARGEBACK_CLAIM_STATUS_MAP.approved
    || claimObject.status === CONSTANTS.CHARGEBACK_CLAIM_STATUS_MAP.waitingForChargeback
    || claimObject.status === CONSTANTS.CHARGEBACK_CLAIM_STATUS_MAP.systemChargeback
    || claimObject.status === CONSTANTS.CHARGEBACK_CLAIM_STATUS_MAP.deactivated
    || !hstore.hasAny(props.user.roles, [CONSTANTS.USER_ROLES.autoClaimsManager, CONSTANTS.USER_ROLES.accounting, CONSTANTS.USER_ROLES.controller]));

  function initialState () {
    return {...props.claimObject};
  }

  const loadFacilityDetails = (facilityID) => {
    ajax(`${apiUrls.facility}/${facilityID}`, {}, {}, (data, status) => {
      if (status === 200) {
        setFacility({...data.facility});
        loadFacilityToClaim(data.facility);
      } else {
        if(data.message) {
          Alert.error(data.message);
        } else {
          Alert.error("Click the browser's Refresh button to reload the claim data. If the error continues, contact your system administrator.");
        }
      }
    });
  };

  const loadDocuments = () => {
    ajax(`api/automotive-claims/${claimObject.id}/documents`, {}, {}, (data, status) => {
      if (status === 200) {
        setClaimObject({...claimObject, documents: data.docs});
      } else {
        if(data.message) {
          Alert.error(data.message);
        } else {
          Alert.error("Click the browser's Refresh button to reload the claim data. If the error continues, contact your system administrator.");
        }
      }
    });
  };

  const handleDeleteAttachment = (toBeDeleted) => {
    if (isDisabled) {
      return;
    }
    ajax(`api/automotive-claims/documents/${toBeDeleted}`, {}, { method: 'DELETE' }, (data, status) => {
      if (status === 200) {
        loadDocuments();
        Alert.success('Document deleted sucessfully.');
      } else {
        if(data.message) {
          Alert.error(data.message);
        } else {
          Alert.error("Click the browser's Refresh button to reload the claim data. If the error continues, contact your system administrator.");
        }
      }
    });
  };

  const loadFacilityToClaim = (facilityDetails) => {
    const claim = {
      ...claimObject, 
      facility_id: facilityDetails.id,
      facility_name: facilityDetails.name,
      facility_address: facilityDetails.address.String,
      facility_postal_code: facilityDetails.postal_code,
      facility_city: facilityDetails.city,
      facility_state_code: facilityDetails.state_code,
      facility_country: facilityDetails.country,
      facility_phone: facilityDetails.phone.String,
      facility_fax: facilityDetails.fax.String
    };
    setClaimObject({...claim});
  };

  const onUseCurrentFacility = (facilityDetails) => {
    loadFacilityToClaim(facilityDetails);
  };

  const closeAttachmentModal = () =>  {
    loadDocuments();
    setShowAttchmentModal(false);
  };

  const showClaimAttachmentModal = () => setShowAttchmentModal(true);

  const submitChargebackClaim = (status) => {
    // Guard against a double-clicked Submit firing the request twice. The backend also
    // serializes concurrent updates, but blocking the second click here keeps the user
    // from seeing a stray "Cannot update claim in this status" warning after the save.
    if (isSubmitting) {
      return;
    }
    setIsSubmitting(true);

    let claimData = { ...claimObject };
    if (status) {
      claimData = {
        ...claimObject,
        status,
      };
    }
    // The paid-date picker shows today's date as its visible default but only writes it to
    // state on an explicit change event. A manual chargeback left with that pre-filled date
    // untouched would otherwise submit an empty chargeback_paid_date and trip the backend
    // "paid date cannot be empty" validation. Capture the displayed default so the submitted
    // value matches what the user sees.
    if (claimData.status === CONSTANTS.CHARGEBACK_CLAIM_STATUS_MAP.manualChargeback && !claimData.chargeback_paid_date) {
      claimData.chargeback_paid_date = toPaidDate(moment());
    }
    ajax(`api/automotive-chargeback-claims/${claimObject.id}`, {...claimData}, { method: 'PUT' }, (data, status) => {
      if (status === 200) {
        Alert.success('Claim submitted successfully.');
        props.closeModal();
      } else {
        setIsSubmitting(false);
        if (data.errors && Object.keys(data.errors).length > 0) {
          for (let key of Object.keys(data.errors)) {
            Alert.warning(data.errors[key]);
          }
        } else if (data.message) {
          Alert.error(data.message);
        } else {
          Alert.error("Click the browser's Refresh button to reload the claim data. If the error continues, contact your system administrator.");
        }
      }
    });
  };

  const reOpenClaim = () => {
    submitChargebackClaim(CONSTANTS.CHARGEBACK_CLAIM_STATUS_MAP.open);
  };

  const getStatusOptions = () => {
    return userHasRole(props.user, CONSTANTS.USER_ROLES.autoClaimsManager) ? optionList.claimStatusManager : optionList.claimStatusAgent;
  };

  const handleStatusChange = (e) => {
    const updated = {
      ...claimObject,
      status: e,
    };
    // Seed the paid date to the picker's visible default (today) the moment the manual
    // chargeback fields become active, so the displayed date is backed by real state
    // instead of only the DatePicker's `selected` fallback.
    if (e === CONSTANTS.CHARGEBACK_CLAIM_STATUS_MAP.manualChargeback && !updated.chargeback_paid_date) {
      updated.chargeback_paid_date = toPaidDate(moment());
    }
    setClaimObject(updated);
  };

  const handleNoteChange = (e) => {
    setClaimObject({
      ...claimObject,
      notes: e.target.value
    });
  };

  const handleUnderwritingChange = (e) => {
    setClaimObject({
      ...claimObject,
      underwriting_chargeback: e,
    });
  };

  const handleUnderwritingNoteChange = (e) => {
    setClaimObject({
      ...claimObject,
      underwriting_chargeback_note: e.target.value,
    });
  };

  // saveUnderwriting validates the manager's chargeback selection. Choosing No or
  // Exception on an Open or Approved chargeback prompts to confirm, because saving
  // deactivates it; on a later status it warns that nothing will be deactivated (TS-18343).
  const saveUnderwriting = () => {
    const value = claimObject.underwriting_chargeback || review.underwriting_default;
    const note = (claimObject.underwriting_chargeback_note || '').trim();

    if (review.underwriting_default === UW.yes && (value === UW.no || value === UW.exception) && !note) {
      Alert.warning('An explanation is required when changing the chargeback selection away from Yes.');
      return;
    }

    // Mirror the server rule (underwritingDeactivates): No/Exception deactivates the
    // chargeback only while it is Open or Approved, before collection starts.
    const willDeactivate = (value === UW.no || value === UW.exception)
      && (claimObject.status === CONSTANTS.CHARGEBACK_CLAIM_STATUS_MAP.open
        || claimObject.status === CONSTANTS.CHARGEBACK_CLAIM_STATUS_MAP.approved);
    if (willDeactivate) {
      setShowUwConfirm(true);
      return;
    }
    if ((value === UW.no || value === UW.exception)
      && claimObject.status !== CONSTANTS.CHARGEBACK_CLAIM_STATUS_MAP.deactivated) {
      Alert.warning(`This chargeback is ${claimObject.status} and will not be deactivated. Please check your selections.`);
    }
    postUnderwriting();
  };

  const postUnderwriting = () => {
    setShowUwConfirm(false);
    const payload = {
      underwriting_chargeback: claimObject.underwriting_chargeback || review.underwriting_default,
      underwriting_chargeback_note: claimObject.underwriting_chargeback_note || '',
    };
    ajax(`api/automotive-chargeback-claims/${claimObject.id}/underwriting-chargeback`, payload, { method: 'PUT' }, (data, status) => {
      if (status === 200) {
        Alert.success('Chargeback selection saved.');
        setClaimObject({
          ...claimObject,
          underwriting_chargeback: data.underwriting_chargeback,
          underwriting_chargeback_note: data.underwriting_chargeback_note,
          status: data.status,
        });
      } else if (data.errors && Object.keys(data.errors).length > 0) {
        for (let key of Object.keys(data.errors)) {
          Alert.warning(data.errors[key]);
        }
      } else if (data.message) {
        Alert.error(data.message);
      } else {
        Alert.error("Click the browser's Refresh button to reload the claim data. If the error continues, contact your system administrator.");
      }
    });
  };

  const handleChargebackCheckNumberChange = (e) => {
    setClaimObject({
      ...claimObject,
      chargeback_check_number: e
    });
  };
  const handleDateChange = (e) => {
    setClaimObject({
      ...claimObject,
      chargeback_paid_date: e ? toPaidDate(e) : e
    });
  };
  const handleChargebackCollectedNoteChange = (e) => {
    setClaimObject({
      ...claimObject,
      chargeback_collected_note: e.target.value
    });
  };

  const handleAmountChange = (e) => {
    if(e) {
      setClaimObject({
        ...claimObject,
        amount: parseFloat(e),
      });
    }
  };

  const hanldePayTypeChange = (e) => {
    if(e) {
      setClaimObject({
        ...claimObject,
        pay_type: e,
      });
    }
  };

  const getDocumentList = () => {
    return claimObject.documents;
  };

  const renderClaimAttachmentModal = () => {
    return (
      <AttachmentModal
        displayModal={ showAttachmentModal }
        closeModal={ closeAttachmentModal }
        claimId={ claimObject.id }/>
    );
  };
  
  const loadPDF = () => {
    window.open(`/api/automotive-claims/${props.claimObject.id}/pdf`);
  };

  const renderClaimModel = () => {
    const disableStatus = (claimObject.status === CONSTANTS.CHARGEBACK_CLAIM_STATUS_MAP.waitingForChargeback
      || claimObject.status === CONSTANTS.CHARGEBACK_CLAIM_STATUS_MAP.systemChargeback
      || initialStatus === CONSTANTS.CHARGEBACK_CLAIM_STATUS_MAP.deactivated
      || initialStatus === CONSTANTS.CHARGEBACK_CLAIM_STATUS_MAP.manualChargeback
    || !hstore.hasAny(props.user.roles, [CONSTANTS.USER_ROLES.autoClaimsManager, CONSTANTS.USER_ROLES.accounting, CONSTANTS.USER_ROLES.controller]));

    const disableChargebackCollection = ( disableStatus ||
    claimObject.status === CONSTANTS.CHARGEBACK_CLAIM_STATUS_MAP.open ||
      claimObject.status === CONSTANTS.CHARGEBACK_CLAIM_STATUS_MAP.approved);

    return (
      // Hidden while either nested modal (attachment or underwriting confirm) is open: the shared
      // Modal hard-codes one z-index, so a visible chargeback modal would show through their
      // overlay (TS-18590, TS-18343).
      <Modal visible={ !showAttachmentModal && !showUwConfirm } close={ props.closeModal } size={ `extra-large` } >
        <div className="col-12 form-group row">
          <div className="col-6">
            <h5 style={{position:'relative', top: -30, marginRight: 45}}>Add Chargeback Claim</h5>
          </div>
          <div className="col-6 text-right">
            {initialStatus === CONSTANTS.CHARGEBACK_CLAIM_STATUS_MAP.deactivated ?
              (<button type="button" className="btn btn-primary" title="Print"
                onClick={reOpenClaim}>
              Re-Open
              </button>) : null}
            &nbsp;&nbsp;&nbsp;
            <button type="button" className="btn btn-secondary" title="Print" 
              onClick={ loadPDF }>
              <i className='fa fa-print'/>
            </button>
          </div>
        </div>

        <div className="col-12 form-group row">
          <div className="col-6">
            <div className="form-group row">
              <div className="col-3 col-form-label col-form-label-sm">
                Pay Type
              </div>
              <SelectBox
                id="pay-type-dropdown"
                value={ claimObject.pay_type }
                disabled={ isDisabled || disableStatus}
                onChange={ hanldePayTypeChange }
                customClassName="form-control-sm"
                optionsList={ optionList.payType }/>
            </div>
            <div className="form-group row" style={ {marginRight: 60}}>
              <If condition={ claimObject.pay_type === CONSTANTS.PAYMENT_TYPE_CODES.CUSTOMER }>
                <PayeeInformation
                  claimObject={claimObject }
                  isDisabled={ isDisabled }
                  showLoader={ () => {} }
                  hideLoader={ () => {} }/>
              </If>
            </div>
            <div className="form-group row">
              <If condition={ claimObject.pay_type === CONSTANTS.PAYMENT_TYPE_CODES.STORE ||claimObject.pay_type === CONSTANTS.PAYMENT_TYPE_CODES.CREDIT_CARD }>
                <FacilityInformation showFacilityLoader={ false }
                  onUseCurrentFacility={ onUseCurrentFacility }
                  claimObject={ claimObject || {}}
                  facilityDetails={ facility || {} }
                  isDisabled={ isDisabled || disableStatus}
                  loadFacilityDetails={ loadFacilityDetails }
                  user={ props.user }/>
              </If>
            </div>
          </div>
          <div className="col-6">
            <div className="form-group row">
              <div className="col-2 col-form-label col-form-label-sm">
                Amount
              </div>
              <div className="col-10">
                <InputBox type="Currency"
                  id={`requested-total-inputBox`}
                  hasDefaultValue={ true }
                  isDisabled={ isDisabled || disableStatus}
                  value={ formatCurrency(claimObject.amount) }
                  onBlur={handleAmountChange}
                />
              </div>
            </div>
            <div className="form-group row">
              <div className="col-2 col-form-label col-form-label-sm">
                Note
              </div>
              <div className="col-10">
                <textarea type="text"
                  id="note-text-area"
                  className="form-control"
                  rows="3"
                  disabled={isDisabled || disableStatus || claimObject.status === CONSTANTS.CHARGEBACK_CLAIM_STATUS_MAP.manualChargeback}
                  value={ claimObject.notes }
                  onChange={ handleNoteChange } />
              </div>
            </div>
            <div className="form-group row">
              <div className="col-2 col-form-label col-form-label-sm">
                Status
              </div>
              <div className="col-10">
                <SelectBox
                  id="status-dropdown"
                  value={ claimObject.status }
                  disabled={ disableStatus && claimObject.status !== CONSTANTS.CHARGEBACK_CLAIM_STATUS_MAP.waitingForChargeback}
                  onChange={ handleStatusChange }
                  customClassName="form-control-sm"
                  optionsList={ getStatusOptions() }/>
              </div>
            </div>
            <div className="form-group row">
              <div className="col-2 col-form-label col-form-label-sm">
                Payment#
              </div>
              <div className="col-10">
                <InputBox type="Text"
                  id="chargeback-collected-text-area"
                  hasDefaultValue={ false }
                  isDisabled={ disableChargebackCollection }
                  value={ claimObject.chargeback_check_number }
                  onChange={ handleChargebackCheckNumberChange } 
                />
              </div>
            </div>
            <div className="form-group row">
              <div className="col-2 col-form-label col-form-label-sm">
                Paid date
              </div>
              <div className="col-10">
                <DatePicker
                  selected={ claimObject.chargeback_paid_date ? paidDateForPicker(claimObject.chargeback_paid_date) : moment() }
                  id={ "amortization_deal_dateInputBox" }
                  dateFormat={dateFormat.displayDateFormat}
                  onChange={ handleDateChange }
                  className="form-control form-control-sm date-field"
                  disabled={disableChargebackCollection}/>
              </div>
            </div>
            <div className="form-group row">
              <div className="col-2 col-form-label col-form-label-sm">
                Chargeback Collected Note
              </div>
              <div className="col-10">
                <textarea type="text"
                  id="chargeback-collected-note-text-area"
                  className="form-control"
                  rows="3"
                  disabled={disableChargebackCollection}
                  value={ claimObject.chargeback_collected_note }
                  onChange={ handleChargebackCollectedNoteChange } />
              </div>
            </div>
            <div className="form-group row">
              <div className="col-2 col-form-label col-form-label-sm">
                Attachments
              </div>
              <div className="col-9">
                <button
                  className={ `btn btn-secondry cursor-pointer` }
                  id={ `chargeback_claim_attachment_btn` }
                  onClick={ showClaimAttachmentModal }
                  disabled={isDisabled}
                >
                  {`Add Attachment`}
                  <i className={ `fa fa-paperclip cursor-pointer text-muted ml-3` }/>
                </button>
                <DocumentLinkList fieldDocs={getDocumentList()}
                  deleteAttachment={ handleDeleteAttachment }
                  claimType={ 'AUTOMOTIVE' }
                  isFinanceUser={ true }/>
              </div>
            </div>
            <If condition={ review.underwriting_applies }>
              <div className="form-group row" id="underwriting-chargeback-section">
                <div className="col-12">
                  <label className="col-form-label-sm font-weight-bold">Underwriting Chargeback</label>
                </div>
                <If condition={ review.days_to_claim_valid }>
                  <div className="col-12 col-form-label-sm" id="chargeback-days-to-claim">
                    Days to Claim: { review.days_to_claim }{ uwIcon(review.days_to_claim_meets) }
                  </div>
                </If>
                <If condition={ review.miles_to_claim_valid }>
                  <div className="col-12 col-form-label-sm" id="chargeback-miles-to-claim">
                    Miles to Claim: { review.miles_to_claim }{ uwIcon(review.miles_to_claim_meets) }
                  </div>
                </If>
                <div className="col-12 col-form-label-sm" id="chargeback-inspection-status">
                  Inspection Status{ uwIcon(review.inspection_meets) }
                </div>
                <div className="col-2 col-form-label col-form-label-sm">
                  Chargeback
                </div>
                <div className="col-10">
                  <SelectBox
                    id="underwriting-chargeback-dropdown"
                    value={ claimObject.underwriting_chargeback || review.underwriting_default }
                    disabled={ !isManager }
                    onChange={ handleUnderwritingChange }
                    customClassName="form-control-sm"
                    optionsList={ underwritingOptions }/>
                </div>
                <div className="col-2 col-form-label col-form-label-sm">
                  Reason
                </div>
                <div className="col-10">
                  <textarea type="text"
                    id="underwriting-chargeback-note"
                    className="form-control"
                    rows="2"
                    disabled={ !isManager }
                    value={ claimObject.underwriting_chargeback_note || '' }
                    onChange={ handleUnderwritingNoteChange } />
                </div>
                <If condition={ isManager }>
                  <div className="col-12 text-right mt-2">
                    <button type="button"
                      className="btn btn-primary btn-sm"
                      id="underwriting-chargeback-save"
                      onClick={ saveUnderwriting }>
                      Save Chargeback Selection
                    </button>
                  </div>
                </If>
              </div>
            </If>
          </div>
        </div>
        <div className="col-12 form-group row">
          <ChargeBackPaymentInfo claimObject={ claimObject }
            initialStatus={ claimObject.status }/>
        </div>
        <div className="form-group row my-4 justify-content-center">
          <button className="btn btn-secondary mr-3"
            onClick={props.closeModal }>
              Cancel
          </button>
          <button className="btn btn-primary"
            disabled={isSubmitting || disableStatus || (initialStatus === CONSTANTS.CHARGEBACK_CLAIM_STATUS_MAP.deactivated)}
            onClick={() => submitChargebackClaim()}>
              Submit
          </button>
        </div>
      </Modal>
    );
  };

  return (
    <div style={{display: "inline"}}>
      {renderClaimModel()}
      {/* Both nested modals render as siblings of the chargeback modal, not inside it: the shared
          Modal's content box uses `transform: translate(-50%, -50%)`, which becomes the containing
          block for a nested `position: fixed` overlay — nesting pinned the overlay to the chargeback
          box and let the chargeback footer bleed through un-dimmed. The chargeback modal is hidden
          while either is open (see visible above). State lives in claimObject, so hiding loses
          nothing (TS-18590 attachment, TS-18343 underwriting confirm). */}
      {showAttachmentModal && renderClaimAttachmentModal()}
      <ConfirmationModal confirmButtonText="Yes"
        declineButtonText="No"
        displayConfirmationModal={ showUwConfirm }
        displayMessage={`Setting the chargeback to "${claimObject.underwriting_chargeback || review.underwriting_default}" will deactivate this chargeback. Please check your selections. Do you want to proceed?`}
        onConfirm={ postUnderwriting }
        onDecline={ () => setShowUwConfirm(false) }/>
    </div>
  );
};

ChargbackClaim.contextTypes = {
  router: PropTypes.object.isRequired,
};
    
ChargbackClaim.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.number.isRequired,
    first_name: PropTypes.string.isRequired,
    last_name: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    stores: PropTypes.arrayOf(PropTypes.string.isRequired),
    roles: PropTypes.shape({
      Map: PropTypes.object
    }).isRequired
  }),
  location: PropTypes.shape({
    query: PropTypes.shape({
      page: PropTypes.string,
      q: PropTypes.string,
      userId: PropTypes.string,
      status: PropTypes.string,
      age: PropTypes.string,
      donutFilter: PropTypes.string,
      sortBy: PropTypes.string,
      sortOrder: PropTypes.string,
      payType: PropTypes.string,
    }).isRequired,
  }).isRequired,
  claimObject: PropTypes.object,
  closeModal: PropTypes.func,
};
  
export default ChargbackClaim;